/**
 * Todo Actions Section Component
 * Displays available actions for a Basecamp todo
 * Supports promotion pattern with SecondaryPanelContent
 */

import { useState } from 'react';
import { RefreshCw, MessageSquare, FileText, Search, Sparkles } from 'lucide-react';
import { BasecampIcon } from '@/components/icons/basecamp-icon';
import { resolveOpenExternalIcon } from '@/lib/open-destination-icons';
import { BASECAMP_OPEN_IN_BASECAMP_LABEL } from '@/lib/basecamp-todo-open-labels';
import type { BasecampTodo } from '@/types/basecamp';
import { SecondaryPanelContent } from '@/components/layout/secondary-panel-content';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { BasecampNotesModal } from './basecamp-notes-modal';
import { buildBasecampWebUrl, normalizeBasecampWebHost } from '@/lib/basecamp-external-url';
import { basecampApi } from '@/lib/basecamp-api';
import { beginBasecampTodoNotesChat } from '@/lib/open-basecamp-todo-notes-chat';
import { beginBasecampTodoActionChat } from '@/lib/open-basecamp-todo-action-chat';
import { ADD_TO_CURRENT_CHAT_LABEL, AddToCurrentChatIcon } from '@/lib/add-to-current-chat';
import { toast } from 'sonner';
import { testIds } from '@/lib/test-ids';

interface TodoAction {
  id: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  onClick: () => void;
  variant?: 'default' | 'outline' | 'destructive' | 'secondary';
}

interface RelatedWorkSpecRef {
  filePath?: string;
  title?: string;
  primary?: boolean;
}

interface TodoActionsSectionProps {
  todo: BasecampTodo;
  projectId?: string;
  projectName?: string;
  todoId: string;
  onIngestOrUpdate?: () => void;
  onDemote?: () => void;
  onDocumentClick?: (documentId: string) => Promise<void>;
  /** Promote Comments secondary panel (Draft a message). */
  onOpenComments?: () => void;
  relatedDocs?: RelatedWorkSpecRef[];
}

/** Resolves a browser Basecamp URL for the todo (see chat-pane triage-analyze payload). */
function getBasecampTodoPageUrl(
  t: BasecampTodo,
  projectId: string | undefined,
  todoId: string
): string | null {
  const url = t.web_url || t.app_url;
  if (url) {
    return normalizeBasecampWebHost(
      url.replace('basecampapi.com', 'basecamp.com').replace('.json', '')
    );
  }
  if (projectId) {
    const accountMatch = String(t.app_url || url || '').match(
      /\/\/(?:3|app)\.basecamp(?:api)?\.com\/(\d+)\//
    );
    const accountId = accountMatch ? accountMatch[1] : '5744549';
    return buildBasecampWebUrl(todoId, projectId, 'todo', accountId);
  }
  return null;
}

function daysOverdueIfAny(dueOn: string | undefined): number | undefined {
  if (!dueOn) return undefined;
  const d = new Date(dueOn + 'T12:00:00');
  const startToday = new Date();
  startToday.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  const diff = Math.floor((startToday.getTime() - d.getTime()) / 86400000);
  return diff > 0 ? diff : undefined;
}

export function TodoActionsSection({
  todo,
  projectId,
  projectName,
  todoId,
  onIngestOrUpdate,
  onDemote,
  onDocumentClick,
  onOpenComments,
  relatedDocs = [],
}: TodoActionsSectionProps) {
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [isGeneratingMarkdown, setIsGeneratingMarkdown] = useState(false);

  const primaryWorkSpecPath =
    relatedDocs.find((d) => d.primary)?.filePath ||
    relatedDocs.find((d) => d.filePath)?.filePath;
  const hasWorkSpec = Boolean(primaryWorkSpecPath);

  const assigneeNames =
    (todo.assignees || [])
      .map((a) => a.name)
      .filter(Boolean)
      .join(', ') ||
    todo.assigned_to?.name ||
    undefined;

  const startTodoActionChat = async (kind: 'draft_comment' | 'find_context') => {
    const todoUrl = getBasecampTodoPageUrl(todo, projectId, todoId);
    if (!todoUrl || !todo.title) {
      toast.error('Could not start action', {
        description: 'Missing Basecamp URL or title. Try refreshing the todo.',
      });
      return;
    }

    if (kind === 'draft_comment') {
      onOpenComments?.();
    }

    const threadId = beginBasecampTodoActionChat({
      kind,
      todoId,
      todoTitle: todo.title,
      contextDocumentId: projectId ? `basecamp-todo-${todoId}` : undefined,
    });

    // Let registry bind the new agent-panel thread before the stream listener runs.
    await new Promise<void>((resolve) => window.setTimeout(resolve, 0));

    window.dispatchEvent(
      new CustomEvent('todo-action-chat-request', {
        detail: {
          kind,
          threadId,
          todoUrl,
          todoTitle: todo.title,
          todoId: todo.id,
          projectId: projectId ?? undefined,
          projectName: projectName ?? undefined,
          workSpecPath: primaryWorkSpecPath,
          assigneeNames,
          dueOn: todo.due_on,
        },
      }),
    );
  };

  /** AI draft in chat -> review -> push to Basecamp todo description (notes). */
  const runUpdateTodoNotes = async () => {
    const threadId = beginBasecampTodoNotesChat({
      todoId,
      todoTitle: todo.title || 'Basecamp todo',
      contextDocumentId: projectId ? `basecamp-todo-${todoId}` : undefined,
    })

    // Let registry bind the new agent-panel thread before routing SSE events.
    await new Promise<void>((resolve) => window.setTimeout(resolve, 0))

    setIsGeneratingMarkdown(true);

    window.dispatchEvent(new CustomEvent('basecamp-notes-generation-start', {
      detail: { todoId, threadId }
    }));

    try {
      const result = await basecampApi.generateBasecampNotesMarkdown(todoId, (event) => {
        window.dispatchEvent(new CustomEvent('thinking-mode-event', {
          detail: { ...event, threadId },
        }));
      });

      window.dispatchEvent(new CustomEvent('basecamp-notes-generation-complete', {
        detail: { result, threadId }
      }));

      if (!result.success || !result.html) {
        toast.error('Failed to generate notes', {
          description: 'No HTML draft returned from server.'
        });
        return;
      }

      const todoUrl = getBasecampTodoPageUrl(todo, projectId, todoId);
      if (!todoUrl) {
        toast.error('Cannot push to Basecamp', {
          description: 'Could not determine the Basecamp URL for this todo.'
        });
        return;
      }

      window.dispatchEvent(new CustomEvent('basecamp-draft-ready', {
        detail: {
          todoId,
          html: result.html,
          todoUrl,
          title: result.title,
          threadId,
        }
      }));
      toast.success('Todo notes draft ready', {
        description: 'Review the draft in chat, then push to update this todo in Basecamp.'
      });
    } catch (error) {
      console.error('[TodoActionsSection] Error generating Basecamp notes:', error);

      window.dispatchEvent(new CustomEvent('basecamp-notes-generation-error', {
        detail: { error: error instanceof Error ? error.message : 'Unknown error', threadId }
      }));

      toast.error('Failed to update todo notes', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsGeneratingMarkdown(false);
    }
  };

  const OpenInBasecampIcon = resolveOpenExternalIcon(BasecampIcon)
  const addToCurrentChat: TodoAction = {
    id: 'add-to-chat',
    label: ADD_TO_CURRENT_CHAT_LABEL,
    description: 'Attach this todo as a chip in the current chat',
    icon: <AddToCurrentChatIcon className="h-4 w-4" />,
    onClick: () => {
      window.dispatchEvent(
        new CustomEvent('add-to-chat', {
          detail: {
            id: `basecamp-todo-${todoId}`,
            title: todo.title,
            name: todo.title,
            documentType: 'note',
            type: 'basecamp-todo',
          },
        }),
      );
      toast.success('Added to chat', { description: todo.title });
    },
    variant: 'outline',
  };

  const createOrUpdateWorkSpec: TodoAction | null = onIngestOrUpdate
    ? {
        id: hasWorkSpec ? 'update-work-spec' : 'create-work-spec',
        label: hasWorkSpec ? 'Update Work Spec' : 'Create work spec',
        description: hasWorkSpec
          ? 'Refresh this todo\'s work spec from Basecamp'
          : 'Create a work spec for this todo',
        icon: <RefreshCw className="h-4 w-4" />,
        onClick: () => {
          onIngestOrUpdate();
        },
        variant: 'outline',
      }
    : null;

  const actions: TodoAction[] = hasWorkSpec
    ? [
        {
          id: 'view-in-basecamp',
          label: BASECAMP_OPEN_IN_BASECAMP_LABEL,
          description: 'Open this todo in Basecamp',
          icon: <OpenInBasecampIcon className="h-4 w-4" />,
          onClick: () => {
            let url = todo.web_url || todo.app_url;
            if (url) {
              url = normalizeBasecampWebHost(
                url.replace('basecampapi.com', 'basecamp.com').replace('.json', '')
              );
              window.open(url, '_blank');
            } else if (projectId && todoId) {
              const accountMatch = String(todo.app_url || '').match(
                /\/\/(?:3|app)\.basecamp(?:api)?\.com\/(\d+)\//
              );
              const accountId = accountMatch ? accountMatch[1] : '5744549';
              window.open(buildBasecampWebUrl(todoId, projectId, 'todo', accountId), '_blank');
            }
          },
          variant: 'outline'
        },
        {
          id: 'assess-work-spec',
          label: 'Assess work spec',
          description: 'Run AI triage analysis in chat (staleness, blockers, next step)',
          icon: <Sparkles className="h-4 w-4" />,
          onClick: () => {
            const todoUrl = getBasecampTodoPageUrl(todo, projectId, todoId);
            if (!todoUrl || !todo.title) {
              toast.error('Could not start assessment', {
                description: 'Missing Basecamp URL or title. Try refreshing the todo.',
              });
              return;
            }
            window.dispatchEvent(
              new CustomEvent('triage-analyze-request', {
                detail: {
                  todoUrl,
                  todoTitle: todo.title,
                  todoId: todo.id,
                  projectId: projectId ?? undefined,
                  projectName: projectName ?? undefined,
                  dueOn: todo.due_on,
                  daysOverdue: daysOverdueIfAny(todo.due_on),
                },
              })
            );
          },
          variant: 'outline'
        },
        ...(createOrUpdateWorkSpec ? [createOrUpdateWorkSpec] : []),
        {
          id: 'draft-message',
          label: 'Draft a message',
          description: 'Shape in Plan mode, then add the body to Comments',
          icon: <MessageSquare className="h-4 w-4" />,
          onClick: () => {
            void startTodoActionChat('draft_comment');
          },
          variant: 'outline'
        },
        {
          id: 'update-todo-notes',
          label: 'Update todo notes',
          description: 'Draft updated notes in chat from the work spec, then push to Basecamp',
          icon: <FileText className="h-4 w-4" />,
          onClick: () => {
            void runUpdateTodoNotes();
          },
          variant: 'outline'
        },
        {
          id: 'find-more-context',
          label: 'Find more context',
          description: 'Search for related documents and information',
          icon: <Search className="h-4 w-4" />,
          onClick: () => {
            void startTodoActionChat('find_context');
          },
          variant: 'outline'
        },
        addToCurrentChat,
      ]
    : [
        ...(createOrUpdateWorkSpec ? [createOrUpdateWorkSpec] : []),
        addToCurrentChat,
      ];

  return (
    <>
      <SecondaryPanelContent
        title="Actions"
        onClose={onDemote}
      >
        {/* @container: hide description lines when the secondary panel is narrow; full text lives in tooltip */}
        <div className="@container p-4 space-y-2 min-w-0">
          {actions.length > 0 ? (
            actions.map((action) => {
              const label =
                action.id === 'update-todo-notes' && isGeneratingMarkdown
                  ? 'Drafting notes...'
                  : action.label;
              const button = (
                <Button
                  data-testid={
                    action.id === 'assess-work-spec'
                      ? testIds.basecamp.todoAssessWorkSpec
                      : action.id === 'add-to-chat'
                        ? testIds.basecamp.todoAddToCurrentChat
                        : action.id === 'update-todo-notes'
                          ? testIds.basecamp.todoUpdateNotes
                          : action.id === 'draft-message'
                            ? testIds.basecamp.todoDraftMessage
                            : action.id === 'find-more-context'
                              ? testIds.basecamp.todoFindContext
                              : undefined
                  }
                  variant={action.variant || 'outline'}
                  className="w-full justify-start gap-2 h-auto py-3 overflow-hidden"
                  onClick={action.onClick}
                  disabled={action.id === 'update-todo-notes' && isGeneratingMarkdown}
                >
                  {action.icon}
                  <div className="flex flex-col items-start flex-1 min-w-0 overflow-hidden">
                    <span className="text-sm font-medium break-words w-full">{label}</span>
                    {action.description && (
                      <span className="hidden @[280px]:block text-xs text-muted-foreground break-words w-full">
                        {action.description}
                      </span>
                    )}
                  </div>
                </Button>
              );

              if (!action.description) {
                return <div key={action.id}>{button}</div>;
              }

              return (
                <Tooltip key={action.id}>
                  <TooltipTrigger asChild>{button}</TooltipTrigger>
                  <TooltipContent side="left" className="max-w-xs text-xs">
                    {action.description}
                  </TooltipContent>
                </Tooltip>
              );
            })
          ) : (
            <p className="text-sm text-muted-foreground italic text-center py-8">
              No actions available
            </p>
          )}
        </div>
      </SecondaryPanelContent>

      <BasecampNotesModal
        todoId={todoId}
        open={showNotesModal}
        onOpenChange={setShowNotesModal}
      />
    </>
  );
}

